// Shared loading skeleton primitive. Three variants — `row`, `card`,
// `text` — all using the same Tailwind `animate-pulse` + `bg-navy-lighter`
// palette to stay visually consistent with the inlined skeletons that
// shipped before extraction (see commit history for IR's old
// `SkeletonRow`). Each variant can be repeated via `count` so call
// sites don't need to wrap the component in a manual `Array.from`.
import type { CSSProperties } from 'react';

type SkeletonVariant = 'row' | 'card' | 'text';

interface SkeletonProps {
  variant: SkeletonVariant;
  /** Number of skeleton instances to render. Defaults to 1. */
  count?: number;
  /** `row` only — CSS grid template for the row's cells. When set, the
   *  row mirrors a real table row's column structure; when omitted, the
   *  row falls back to a generic flex layout suitable for non-tabular
   *  loading states (e.g. response lists without a fixed grid). */
  gridTemplateColumns?: string;
  /** Optional className applied to each instance's outer wrapper. */
  className?: string;
}

export function Skeleton({
  variant,
  count = 1,
  gridTemplateColumns,
  className,
}: SkeletonProps): JSX.Element {
  const items = Array.from({ length: Math.max(1, count) }, (_, i) => i);
  return (
    <>
      {items.map(i => {
        if (variant === 'row') {
          return (
            <SkeletonRow
              key={i}
              gridTemplateColumns={gridTemplateColumns}
              className={className}
            />
          );
        }
        if (variant === 'card') {
          return <SkeletonCard key={i} className={className} />;
        }
        return <SkeletonText key={i} className={className} />;
      })}
    </>
  );
}

function SkeletonRow({
  gridTemplateColumns,
  className,
}: {
  gridTemplateColumns?: string;
  className?: string;
}): JSX.Element {
  // Grid mode mirrors the table column structure exactly so the
  // skeleton lines line up with real header columns. Eight cells matches
  // the IR table (Date | Type | Family | Bates | AI Summary | Subject |
  // From | Open) — the AI-Summary cell uses two stacked bars to suggest
  // multi-line content. For non-grid (generic) callers, we fall back to
  // a flex row with three bars sized to look like a typical list row.
  if (gridTemplateColumns) {
    const style: CSSProperties = { gridTemplateColumns };
    return (
      <div
        className={
          'grid gap-2 px-5 py-3 border-b border-navy-lighter animate-pulse' +
          (className ? ` ${className}` : '')
        }
        style={style}
        aria-hidden
      >
        <div className="h-3 rounded bg-navy-lighter/80" />
        <div className="h-3 rounded bg-navy-lighter/80" />
        <div className="h-3 rounded bg-navy-lighter/60" />
        <div className="h-3 rounded bg-navy-lighter/80" />
        <div className="space-y-1.5">
          <div className="h-2.5 rounded bg-navy-lighter/80 w-full" />
          <div className="h-2.5 rounded bg-navy-lighter/70 w-4/5" />
        </div>
        <div className="h-3 rounded bg-navy-lighter/80" />
        <div className="h-3 rounded bg-navy-lighter/80" />
        <div className="h-3 rounded bg-navy-lighter/60" />
      </div>
    );
  }
  return (
    <div
      className={
        'flex items-center gap-4 px-5 py-3 border-b border-navy-lighter animate-pulse' +
        (className ? ` ${className}` : '')
      }
      aria-hidden
    >
      <div className="h-3 rounded bg-navy-lighter/80 w-32 shrink-0" />
      <div className="flex-1 space-y-1.5">
        <div className="h-2.5 rounded bg-navy-lighter/80 w-full" />
        <div className="h-2.5 rounded bg-navy-lighter/70 w-3/4" />
      </div>
      <div className="h-3 rounded bg-navy-lighter/60 w-20 shrink-0" />
    </div>
  );
}

function SkeletonCard({ className }: { className?: string }): JSX.Element {
  // Mirrors the visual rhythm of CollectionsDashboard cards: an icon
  // square in the top-left, an arrow-ish square in the top-right, then
  // a title bar and a smaller meta bar at the bottom.
  return (
    <div
      className={
        'bg-navy-light border border-navy-lighter rounded-xl p-5 flex flex-col gap-4 animate-pulse' +
        (className ? ` ${className}` : '')
      }
      aria-hidden
    >
      <div className="flex items-start justify-between gap-3">
        <div className="w-9 h-9 rounded-lg bg-navy-lighter/80" />
        <div className="w-4 h-4 rounded bg-navy-lighter/60 mt-1" />
      </div>
      <div className="space-y-2">
        <div className="h-3.5 rounded bg-navy-lighter/80 w-3/5" />
        <div className="h-2.5 rounded bg-navy-lighter/60 w-2/5" />
      </div>
    </div>
  );
}

function SkeletonText({ className }: { className?: string }): JSX.Element {
  return (
    <div
      className={
        'h-3 rounded bg-navy-lighter/80 animate-pulse' +
        (className ? ` ${className}` : '')
      }
      aria-hidden
    />
  );
}
